import React, { useEffect, useState } from "react";
import "./NewsForm.css";
import { newsService } from "../../services/newsService";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import MediaLibraryModal from "../../components/common/MediaLibrary/MediaLibraryModal";


const NewsForm = ({ newsId, onSubmitted, onCancel }) => {
  const id = newsId;

  const [formData, setFormData] = useState({
    publisher: "",
    title: "",
    link: "",
    publication_date: "",
    publish_time: "",
    brief_description: "",
    news_content: "",
    tags_stocks: [],
    image_url: null,
    credit: "",
    source_url: "",
    destination_url: "",
    tags_sectors: [],
    tags_industries: [],
    news_category: "",
    ntf_type: "",
    premium: false,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [internalTokens, setInternalTokens] = useState([]);
  const [tagSectors, setTagSectors] = useState([]); 
  const [tagIndustries, setTagIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newsCategory, setNewsCategory] = useState("");
  const [fetchingNews, setFetchingNews] = useState(false);
  const [newsFetchError, setNewsFetchError] = useState(null);


  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file) {
        setFormData({ ...formData, image_url: file });
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMediaSelect = (imageName) => {
    setFormData({ ...formData, image_url: imageName });
    setImagePreview(`${REACT_APP_API_BASE_URL}uploads/newsimages/${imageName}`);
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, news_content: content });
  };

  const handlePremiumChange = (e) => {
    setFormData({ ...formData, premium: e.target.checked });
  };

  const normalizeHtml = (html = "") =>
    String(html)
      .replace(/&nbsp;/g, " ")
      .replace(/\u00a0/g, " ")
      .trim();

  const fetchInternalTokens = async () => {
    try {
      const response = await newsService.getInternalTokens();
      setInternalTokens(response.data);
    } catch (error) {
      console.error("Error fetching internal tokens:", error);
    }
  };

  const fetchNotificationTypes = async () => {
    try {
      const response = await newsService.getAllNotificationTypes();
      setNotificationTypes(response || []); 
    } catch (error) {
      console.error("Error fetching notification types:", error);
    }
  };

  const fetchTagSectors = async () => {
    try {
      const response = await newsService.getTagSectors();
      setTagSectors(response.data || []);
    } catch (error) {
      console.error("Error fetching sectors:", error);
    }
  }

  const fetchTagIndustries = async () => {
    try {
      const response = await newsService.getTagIndustries();
      setTagIndustries(response.data || []);
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await newsService.getCategories();
      // Assuming response.categories based on controller
      setCategories(response.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchInternalTokens();
    fetchTagSectors();
    fetchTagIndustries();
    fetchCategories();
    fetchNotificationTypes();
  }, []);


  useEffect(() => {
    let isMounted = true;

    const fetchNews = async () => {
      if (!id) return;

      setFetchingNews(true);
      setNewsFetchError(null);

      try {
        const response = await newsService.getNewsById(id);
        
        if (!isMounted) return;

        let article;
        if (response && typeof response === 'object') {
          if (response.data) {
            article = response.data;
          } else if (response.news) {
            article = response.news;
          } else {
            article = response;
          }
        }

        if (!article || typeof article !== 'object') {
          console.error("Invalid news article data received:", response);
          setNewsFetchError("Invalid data format received from server");
          return;
        }


          // Format date for input field
          if (article.publication_date) {
            article.publication_date = article.publication_date.split("T")[0];
          }

          setIsPublished(article?.ispublished === 1);

          // Parse JSON tags if they are strings (Postgres JSON columns might come as strings or objects)
          const parseTags = (tags) => {
            if (!tags) return [];
            if (Array.isArray(tags)) return tags;
            try { return JSON.parse(tags); } catch (e) { return []; }
          };

          const tags_stocks = parseTags(article.tags_stocks || article.tag_stocks);
          const tags_sectors = parseTags(article.tags_sectors || article.tag_sector);
          const tags_industries = parseTags(article.tags_industries || article.tag_industry);

        setFormData({
          publisher: article.publisher || "",
          title: article.title || "",
          link: article.link || "",
          publication_date: article.publication_date || "",
          publish_time: article.publish_time || "",
          brief_description: article.brief_description || "",
          news_content: article.news_content || "",
          tags_stocks: tags_stocks,
          tags_sectors: tags_sectors,
          tags_industries: tags_industries,
          credit: article.credit || "",
          source_url: article.source_url || "",
          destination_url: article.destination_url || "",
          news_category: article.news_category || "",
          image_url: article.image_url || null,
          ntf_type: article.ntf_type || null,
          premium: article.premium || false,
        }); 

        if (article.image_url) {
          setImagePreview(`${REACT_APP_API_BASE_URL}uploads/newsimages/${article.image_url}`);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching news article:", error);
        setNewsFetchError(error.message || "Failed to load news data");
        toast.error("❌ Failed to load news data");
      } finally {
        if (isMounted) {
          setFetchingNews(false);
        }
      }
    };

    fetchNews();

    return () => {
      isMounted = false;
    };
  }, [id]);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.news_category) {
      toast.error("Please select a news category");
      return;
    }

    const action = e.nativeEvent.submitter?.value || "update";
    const isPublish = action === "publish";

    const data = new FormData();

    // Convert tags to JSON array safely
    const convertToJSONArray = (value) => {
      if (!value) return "[]";

      // If value is already an array, return directly
      if (Array.isArray(value)) {
        return JSON.stringify(value.filter(item => item !== ""));
      }

      // If it is a string, split it
      return JSON.stringify(
        value
          .split(",")
          .map(item => item.trim())
          .filter(item => item !== "")
      );
    };


    data.append("publisher", formData.publisher);
    data.append("title", formData.title);
    data.append("link", formData.link);
    data.append("publication_date", formData.publication_date);
    data.append("publish_time", formData.publish_time);
    data.append("brief_description", formData.brief_description);
    data.append("news_content", normalizeHtml(formData.news_content));
    data.append("news_category", formData.news_category);
    data.append("credit", formData.credit);
    data.append("source_url", formData.source_url);
    data.append("destination_url", formData.destination_url);
    data.append("premium", formData.premium || false);

    data.append("tags_stocks", convertToJSONArray(formData.tags_stocks));
    data.append("tags_sectors", convertToJSONArray(formData.tags_sectors));
    data.append("tags_industries", convertToJSONArray(formData.tags_industries));

    if (isPublish) {
      const userId = localStorage.getItem("userId");
      data.append("published_by", userId);
    }

    if (formData.image_url instanceof File) {
      data.append("image_url", formData.image_url);
    } else if (typeof formData.image_url === "string") {
      data.append("image_url", formData.image_url);
    }

    try {

      if (id) {
        await newsService.updateNews(id, data);
        toast.success(
          isPublish
            ? "News published successfully"
            : "News updated successfully"
        );
      } else {
        await newsService.createNews(data);
        toast.success(
          isPublish
            ? "News created and published successfully"
            : "News created successfully"
        );
      }

      if (isPublish && formData.ntf_type) {
        // Send notification for selected type
        const ntfType = notificationTypes.find(t => t.id === formData.ntf_type);
        if (ntfType) {
          await handleSendNotification(
            1, // Default ID for news notification
            `${newsCategory} Update`,
            formData.title,
            ntfType.id,
            4, // Notification Type ID for News
            id // Article ID for deep linking
          );
        }
      }
      if (onSubmitted) onSubmitted();
    } catch (error) {
      // api.js returns a formatted error object with { message, status, data }
      const msg = error.message || "Error posting news";
      toast.error("❌ " + msg);
    }
  };

  // const publishHandler = async () => {
  //   try {
  //     const userId = localStorage.getItem("userId");

  //     const payload = {}
  //     payload.published_by = userId;

  //     await newsService.isPublishNews(id, payload);
  //     toast.success("✅ News Published successfully");
  //     navigate("/newsfeed");
  //   } catch (error) {
  //     toast.error("Error publishing")
  //   }
  // }



  

  const handleSendNotification = async (
    id,
    title,
    message,
    query,
    notificationtypeid,
    newsId,
  ) => {
    if (!id) return;

    try {
      setLoading(true);

      const payload = {
        id,
        title,
        message,
        query,
        notificationtypeid,
        ...(newsId ? { newsId } : {}),
      };

      const response = await newsService.sendNotification(payload);
      toast.success(
        response.data?.message ||
        `Notification sent successfully to ${response.sent} users!`,
      );
      return;
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  if (id && fetchingNews) {
    return (
      <div className="news-container">
        <div className="flex-center" style={{ fontSize: '18px', color: '#5f6368', minHeight: '60vh' }}>
          Loading news data...
        </div>
      </div>
    );
  }

  if (id && newsFetchError) {
    return (
      <div className="news-container">
        <div className="flex-center-col" style={{ minHeight: '60vh', padding: '20px', textAlign: 'center' }}>
          <h2 className="error-title">⚠️ Error Loading News</h2>
          <p className="error-message">{newsFetchError}</p>
          <button 
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="news-container">
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className="relative-container">
        <form className="news-card" onSubmit={handleSubmit}>
          {loading && <div className="loading-overlay">Sending Notification...</div>}
          <div className="image-section-container">
            <label className="section-label">Featured Image</label>
            <div className="image-section" onClick={() => document.getElementById('imageInput').click()}>
              {imagePreview ? (
                <div className="image-preview-container">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="preview-image"
                  />
                </div>
              ) : (
                <div className="image-placeholder">
                  <span>Upload a Featured Image</span>
                </div>
              )}

              <input
                id="imageInput"
                type="file"
                accept="image/*"
                name="image_url"
                onChange={handleChange}
                className="image-input display-none"
              />
            </div>

            <div className="image-actions">
              <button
                type="button"
                className="media-library-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMediaLibraryOpen(true);
                }}
              >
                📁 Browse Media Library
              </button>

              {imagePreview && (
                <button
                  type="button"
                  className="clear-image-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImagePreview(null);
                    setFormData({ ...formData, image_url: null });
                    const input = document.getElementById("imageInput");
                    if (input) input.value = "";
                  }}
                >
                  ✖ Remove Image
                </button>
              )}
            </div>
          </div>


          <div className="form-grid">
            <div className="form-group">
              <label>Category <span className="text-red"></span></label>
              <Select
                name="news_category"
                value={
                  categories
                    ? categories
                      .map((cat) => ({
                        label: cat.name,
                        value: cat.id
                      }))
                      .find((option) => option.value == formData.news_category) || null
                    : null
                }
                onChange={(selectedOption) => {
                  setFormData({
                    ...formData,
                    news_category: selectedOption ? selectedOption.value : "",
                  });
                  setNewsCategory(selectedOption ? selectedOption.label : "");
                }}
                options={categories.map((cat) => ({
                  label: cat.name,
                  value: cat.id,
                }))}
                placeholder="Select Category..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
                required
              />
            </div>

            <div className="form-group">
              <label>Publisher</label>
              <input
                name="publisher"
                value={formData.publisher}
                onChange={handleChange}
                placeholder="Enter publisher name"

              />
            </div>

            <div className="form-group">
              <label>Credit</label>
              <input
                name="credit"
                value={formData.credit}
                onChange={handleChange}
                placeholder="Credit (e.g. Author / Agency)"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter an eye-catching headline"
            />
          </div>

          <div className="form-group">
            <label>Brief Description</label>
            <textarea
              name="brief_description"
              rows="2"
              value={formData.brief_description}
              onChange={handleChange}
              placeholder="Short summary of the news..."
              required
            />
          </div>

          <div className="form-group quill-editor-group">
            <label>Full News Content</label>
            <ReactQuill
              theme="snow"
              value={formData.news_content}
              onChange={handleEditorChange}
              placeholder="Write or paste full news content here..."
              modules={{
                toolbar: [
                 [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link', 'image', 'video'],
                  [{ align: ['justify'] }], // ✅ FIXED
                  ['clean'],
                ],
              }}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Link</label>
              <input
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="Original article link"
              />
            </div>

            <div className="form-group">
              <label>Source URL</label>
              <input
                name="source_url"
                value={formData.source_url}
                onChange={handleChange}
                placeholder="Source link"
              />
            </div>

            <div className="form-group">
              <label>Destination URL</label>
              <input
                name="destination_url"
                value={formData.destination_url}
                onChange={handleChange}
                placeholder="Redirect destination"
              />
            </div>

          </div>

            <div className="form-grid">
            <div className="form-group">
              <label>Publication Date</label>
              <input
                type="date"
                name="publication_date"
                value={formData.publication_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Publish Time</label>
              <input
                type="time"
                name="publish_time"
                value={formData.publish_time}
                onChange={handleChange}
                required
              />
            </div>

        


      <div className="form-group">
  <label>Notification Type</label>
  <Select
    name="tags_notification_type_id"
    value={
      formData.ntf_type
        ? {
            label:
              notificationTypes.find((item) => item.id === formData.ntf_type)
                ?.query_title || "",
            value: formData.ntf_type,
          }
        : null
    }
    onChange={(selectedOption) =>
      setFormData({
        ...formData,
        ntf_type: selectedOption ? selectedOption.value : null,
      })
    }
    options={notificationTypes.map((item) => ({
      label: item.query_title || "",
      value: item.id,
    }))}
    placeholder="Select Notification Type..."
    menuPortalTarget={document.body}
    menuPosition="fixed"
    styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
    classNamePrefix="react-select"
    isSearchable
  />
</div>

          </div>

          <h3 className="tags-heading">Tags</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Stocks</label>
              <Select
                name="tags_stocks"
                isMulti
                value={
                  formData.tags_stocks && Array.isArray(formData.tags_stocks)
                    ? internalTokens
                      .map((token) => ({
                        label: `${token.symbol} (${token.name})`,
                        value: token.token
                      }))
                      .filter((option) => formData.tags_stocks.includes(option.value))
                    : []
                }
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_stocks: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={internalTokens.map((token) => ({
                  label: `${token.symbol} (${token.name})`,
                  value: token.token,
                }))}
                placeholder="Select Stocks..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
                // required
              />
            </div>
            <div className="form-group">
              <label>Sectors</label>
              <Select
                name="tags_sectors"
                isMulti
                value={
                  formData.tags_sectors && Array.isArray(formData.tags_sectors)
                    ? tagSectors
                      .map((item) => ({
                        label: item.sector_name || item,
                        value: item.sector_name || item
                      }))
                      .filter((option) => formData.tags_sectors.includes(option.value))
                    : []
                }
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_sectors: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={tagSectors.map((item) => ({
                  label: item.sector_name || item,
                  value: item.sector_name || item,
                }))}
                placeholder="Select Sectors..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
            <div className="form-group">
              <label>Industries</label>
              <Select
                name="tags_industries"
                isMulti
                value={
                  formData.tags_industries && Array.isArray(formData.tags_industries)
                    ? tagIndustries
                      .map((item) => ({
                        label: item.industry_name || item,
                        value: item.industry_name || item
                      }))
                      .filter((option) => formData.tags_industries.includes(option.value))
                    : []
                }
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_industries: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={tagIndustries.map((item) => ({
                  label: item.industry_name || item,
                  value: item.industry_name || item,
                }))}
                placeholder="Select Industries..."
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
                classNamePrefix="react-select"
                isSearchable
              />
            </div>
        
          </div>

          <div className="form-group form-group-premium">
            <label className="premium-flex">
              <input
                type="checkbox"
                id="premium"
                name="premium"
                checked={!!formData.premium}
                onChange={handlePremiumChange}
                className="premium-checkbox"
              />
              <span className="premium-label">
                Premium news
              </span>
            </label>
          </div>

          <div className="flex-space-around">
            <button type="button" className="submit-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" >
              {id ? 'Update' : 'Save'}
            </button>
            {id && (<button type="submit" className="publish-btn" value="publish" >
              Publish
            </button>)}
          </div>
        </form>
      </div>

      <MediaLibraryModal
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
      />
    </div>
  );
};

export default NewsForm;
